import styled from '@emotion/styled'
import { useBreakpoint } from '@pharmac-extranet/shared'
import { useEffect, useRef, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { LanguageProvider } from '../../i18n/context/LanguageContextProvider'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'
import { NotLoggedInView } from '../../views/not-logged-in/NotLoggedInView'
import { AdminProfessionalRoleSelectModal } from '../admin-professional-role-select/AdminProfessionalRoleSelectModal'
import { CustomerSelector } from '../customer-service/CustomerSelector'
import { InactivityModal } from '../inactivity-modal/InactivityModal'
import { NavigationBar } from '../navigation'
import { DesktopNavigation } from '../navigation/DesktopNavigation'
import { MobileNavigation } from '../navigation/MobileNavigation'
import { SelectCustomerModal } from '../select-customer-modal/SelectCustomerModal'
import { UserApprovalModal } from '../user/ApprovalModal'
import { ExtranetFooter } from './ExtranetFooter'
import { ExtranetLoading } from './ExtranetLoading'
import { ExtranetWrapper } from './ExtranetWrapper'

const ExtranetRootComponent = styled.div`
  padding: 0;
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`

const ExtranetRoot = () => {
  const location = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  const createLink = useExtranetPath()

  const {
    isLoggedIn,
    hasInitialized: acHasInitialized,
    hasAcceptedPrivacyPolicy,
    hasAcceptedTermsAndConditions,
    isPharmacCustomerService,
    isPharmacUser,
    user,
  } = useAuthenticationContext()

  const {
    hasInitialized: ccHasInitialized,
    showModal,
    userCustomers,
    customer,
  } = useCustomerContext()
  const [showRoleModal, setShowRoleModal] = useState<boolean>(true)

  const { minDesktop } = useBreakpoint()

  const hasInitialized = acHasInitialized && ccHasInitialized
  const userHasCustomers = hasInitialized && userCustomers.length > 0
  const hasPharmacUserFlags = acHasInitialized && (isPharmacCustomerService || isPharmacUser)

  console.log('ExtranetRoot :: DEBUG :: ', { hasInitialized, userHasCustomers, showModal })

  const embedUrlRef = useRef<null | string>(null)

  useEffect(() => {
    let mounted = true

    const chatTokenHandler = async (data: any) => {
      const widget = data.detail.widget

      if (customer && customer.id) {
        const userChatToken = await api.chat.getChatToken(customer.id)

        widget?.events.addListener('open', () => {
          widget.sendCustomData({ token: userChatToken })
        })
      }
    }

    if (hasInitialized && isLoggedIn && customer && embedUrlRef.current === null) {
      api.chat.getChatEmbedUrl().then((response) => {
        if (response?.embedUrl && mounted) {
          embedUrlRef.current = response.embedUrl

          const scriptTag = document.createElement('script')
          scriptTag.src = response.embedUrl
          document.body.appendChild(scriptTag)
        }
      })
    }
    document.addEventListener('inteliwiseWidgetReady', chatTokenHandler)

    return () => {
      document.removeEventListener('inteliwiseWidgetReady', chatTokenHandler)
      mounted = false
    }
  }, [hasInitialized, customer])

  if (acHasInitialized && !isLoggedIn) {
    return <NotLoggedInView />
  }

  const hasApprovedPolicies = hasAcceptedPrivacyPolicy && hasAcceptedTermsAndConditions

  if (hasInitialized && !hasApprovedPolicies && !hasPharmacUserFlags) {
    return <UserApprovalModal />
  }

  if (hasInitialized && showModal && !hasPharmacUserFlags) {
    return <SelectCustomerModal />
  }

  if (
    hasInitialized &&
    !hasPharmacUserFlags &&
    userHasCustomers &&
    user?.professionalRoles.length === 0 &&
    !user.professionalRolesSelected &&
    showRoleModal
  ) {
    return (
      <AdminProfessionalRoleSelectModal
        setShowRoleModal={() => {
          setShowRoleModal(false)
        }}
      />
    )
  }

  if (hasInitialized && !userHasCustomers && !hasPharmacUserFlags) {
    return <Navigate to={createLink('unauthorized')} />
  }

  return (
    <LanguageProvider>
      {!hasInitialized && <ExtranetLoading />}
      {hasInitialized && (
        <>
          <ExtranetRootComponent>
            <NavigationBar>
              {minDesktop ? <DesktopNavigation /> : <MobileNavigation />}
            </NavigationBar>

            {isPharmacCustomerService && (
              <ExtranetWrapper>
                <CustomerSelector />
              </ExtranetWrapper>
            )}

            <Outlet />

            <ExtranetFooter />
          </ExtranetRootComponent>
          <InactivityModal />
        </>
      )}
    </LanguageProvider>
  )
}

export default ExtranetRoot
